import React, { useState } from 'react';
import { Tab, TabList, TabPanel, Tabs } from 'carbon-react/lib/components/tabs/__next__';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';

import * as styles from './organisation-overview-dashboard.scss';
import { useFirstName } from './hooks';
import { DashboardHeader } from './components/dashboard-header/dashboard-header';
import { PerformanceTabContainer } from './components/performance-tab/performance-tab-container';
import { CustomersAndSuppliersTab } from './components/customers-and-suppliers-tab/customers-and-suppliers-tab';
import { useNavHeight } from '../hooks/useNavHeight';
import { AiInsightsContainer } from '../ai-insights/ai-insights-container';

const BUSINESS_ANALYSIS_TAB = 'business-analysis';
const BUSINESS_INSIGHTS_TAB = 'business-insights';
const CUSTOMERS_AND_SUPPLIERS_TAB = 'customers-and-suppliers';

const BUSINESS_ANALYSIS_PANEL = `${BUSINESS_ANALYSIS_TAB}-panel`;
const BUSINESS_INSIGHTS_PANEL = `${BUSINESS_INSIGHTS_TAB}-panel`;
const CUSTOMERS_AND_SUPPLIERS_PANEL = `${CUSTOMERS_AND_SUPPLIERS_TAB}-panel`;

export const OrganisationOverviewDashboard = () => {
  const { navHeight } = useNavHeight();
  const flags = useFlags();
  const intl = useIntl();
  const firstName = useFirstName();

  const [selectedTab, setSelectedTab] = useState(flags.aiInsights ? BUSINESS_ANALYSIS_TAB : BUSINESS_INSIGHTS_TAB);
  const [hasVisitedBusinessInsightsTab, setHasVisitedBusinessInsightsTab] = useState(!flags.aiInsights);
  const [hasVisitedCustomersTab, setHasVisitedCustomersTab] = useState(false);

  return (
    <div>
      <DashboardHeader firstName={firstName} />
      <div className={styles.content}>
        <div
          className={classNames(styles.tabsContainer, styles.carbonTabsContainer)}
          style={{ '--nav-height': `${navHeight}px` } as React.CSSProperties}
        >
          <Tabs selectedTabId={selectedTab} size="large">
            <TabList
              ariaLabel={intl.formatMessage({ id: 'predict.dashboard_tabs', defaultMessage: 'Dashboard tabs' })}
              onTabChange={(tabId: string) => {
                setSelectedTab(tabId);
                if (tabId === BUSINESS_INSIGHTS_TAB) {
                  setHasVisitedBusinessInsightsTab(true);
                }
                if (tabId === CUSTOMERS_AND_SUPPLIERS_TAB) {
                  setHasVisitedCustomersTab(true);
                }
              }}
            >
              {flags.aiInsights && (
                <Tab
                  id={BUSINESS_ANALYSIS_TAB}
                  controls={BUSINESS_ANALYSIS_PANEL}
                  label={intl.formatMessage({ id: 'common.business_analysis', defaultMessage: 'Business analysis' })}
                />
              )}
              <Tab
                id={BUSINESS_INSIGHTS_TAB}
                controls={BUSINESS_INSIGHTS_PANEL}
                label={intl.formatMessage({ id: 'reporting.performance', defaultMessage: 'Performance' })}
              />
              <Tab
                id={CUSTOMERS_AND_SUPPLIERS_TAB}
                controls={CUSTOMERS_AND_SUPPLIERS_PANEL}
                label={intl.formatMessage({
                  id: 'predict.customers_and_suppliers',
                  defaultMessage: 'Customers & Suppliers'
                })}
              />
            </TabList>
            {flags.aiInsights && (
              <TabPanel id={BUSINESS_ANALYSIS_PANEL} tabId={BUSINESS_ANALYSIS_TAB}>
                <AiInsightsContainer isActive={selectedTab === BUSINESS_ANALYSIS_TAB} />
              </TabPanel>
            )}
            <TabPanel id={BUSINESS_INSIGHTS_PANEL} tabId={BUSINESS_INSIGHTS_TAB}>
              {hasVisitedBusinessInsightsTab && <PerformanceTabContainer />}
            </TabPanel>
            <TabPanel id={CUSTOMERS_AND_SUPPLIERS_PANEL} tabId={CUSTOMERS_AND_SUPPLIERS_TAB}>
              {hasVisitedCustomersTab && <CustomersAndSuppliersTab />}
            </TabPanel>
          </Tabs>
        </div>
      </div>
    </div>
  );
};
